import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";

import ListPagination from "../../../containers/ListPagination";
import { PAGES_TITLES } from "../../../constants/titles";
import { GET_GOVERNING_BODIES_ADMIN_ALL } from "../../../constants/endpoints";
import { getList } from "../../../dataProvider/API";
import { normalizeGoverningBodiesPayload } from "../../../utils/governingBodyApi";
import { rulesApi } from "../../../utils/rulesApi";
import {
  AuditStamp,
  Breadcrumbs,
  rulesPath,
  isParkedGroup,
  sortDirection,
  StatusChip,
  useDebouncedCommit,
} from "../common";
import { useRuleStyles } from "../styles";
import { keepState, returnState } from "../../../utils/returnTo";
import RuleGroupDialog from "./RuleGroupDialog";

const DEFAULT_SIZE = 20;
// Newest group first: ids are assigned in creation order and the parked "do not use" groups are old.
const DEFAULT_SORT = "Id";
const DEFAULT_ASCENDING = false;

const SORTABLE = [
  { key: "Id", label: "ID" },
  { key: "Name", label: "Name" },
];

const readQuery = (search) => {
  const sp = new URLSearchParams(search);
  return {
    query: sp.get("query") || "",
    governingBodyId: sp.get("governingBodyId") || "",
    status: sp.get("status") || "ACTIVE",
    sort: sp.get("sort") || DEFAULT_SORT,
    isAscending: sp.has("isAscending")
      ? sp.get("isAscending") !== "false"
      : DEFAULT_ASCENDING,
    page: Number(sp.get("page")) || 0,
    size: Number(sp.get("size")) || DEFAULT_SIZE,
  };
};

const writeQuery = (state) => {
  const sp = new URLSearchParams();
  if (state.query) sp.set("query", state.query);
  if (state.governingBodyId) sp.set("governingBodyId", state.governingBodyId);
  if (state.status !== "ACTIVE") sp.set("status", state.status);
  if (state.sort !== DEFAULT_SORT) sp.set("sort", state.sort);
  if (state.isAscending !== DEFAULT_ASCENDING)
    sp.set("isAscending", String(state.isAscending));
  if (state.page) sp.set("page", String(state.page));
  if (state.size !== DEFAULT_SIZE) sp.set("size", String(state.size));
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
};

const EMPTY_PAGE = { items: [], totalElements: 0, currentPage: 0 };

/** Screen 1: rule groups, the containers meet setup offers per governing body. */
const RuleGroups = () => {
  const classes = useRuleStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = useState(() => readQuery(location.search));
  const [searchDraft, setSearchDraft] = useState(state.query);
  const [governingBodies, setGoverningBodies] = useState([]);
  const [result, setResult] = useState(EMPTY_PAGE);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, group: null });

  useEffect(() => {
    getList(
      GET_GOVERNING_BODIES_ADMIN_ALL({
        page: 0,
        limit: 200,
        sort: "Name",
        isAscending: true,
        status: "ACTIVE",
      }),
    )
      .then((payload) =>
        setGoverningBodies(
          normalizeGoverningBodiesPayload(payload, "en-US").rows,
        ),
      )
      .catch(() => setGoverningBodies([]));
  }, []);

  useDebouncedCommit(searchDraft, (query) =>
    setState((prev) =>
      prev.query === query ? prev : { ...prev, query, page: 0 },
    ),
  );

  const load = useCallback(() => {
    setLoading(true);
    rulesApi
      .groups(state)
      .then((data) => setResult(data || EMPTY_PAGE))
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [state]);

  useEffect(() => {
    load();
    const nextSearch = writeQuery(state);
    if (nextSearch !== location.search) {
      navigate(
        { pathname: location.pathname, search: nextSearch },
        { replace: true, state: keepState(location) },
      );
    }
    // location is intentionally not a dependency: this effect writes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const patch = (changes) =>
    setState((prev) => ({ ...prev, page: 0, ...changes }));

  const onSort = (key) =>
    setState((prev) => ({
      ...prev,
      page: 0,
      sort: key,
      isAscending: prev.sort === key ? !prev.isAscending : true,
    }));

  const rows = (result.items || []).filter(
    (row) => state.status !== "ACTIVE" || !isParkedGroup(row),
  );

  return (
    <Box className={classes.page}>
      <Container maxWidth="xl">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Rules" }]} />
        <Box className={classes.header}>
          <Box>
            <Typography component="h1" className={classes.title}>
              {PAGES_TITLES.RULES}
            </Typography>
            <Typography className={classes.subtitle}>
              A rule group is the list of rule sets a governing body can pick
              from when an event is set up. Open a group to maintain its rule
              sets.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            className={classes.touch}
            onClick={() => setDialog({ open: true, group: null })}
          >
            New rule group
          </Button>
        </Box>

        <Paper
          className={classes.filters}
          component="section"
          aria-label="Filters"
        >
          <TextField
            label="Search"
            placeholder="Name or id"
            variant="outlined"
            size="small"
            className={classes.filterFieldWide}
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            inputProps={{ "aria-label": "Search rule groups" }}
          />
          <TextField
            select
            label="Governing body"
            variant="outlined"
            size="small"
            className={classes.filterField}
            value={state.governingBodyId}
            onChange={(e) => patch({ governingBodyId: e.target.value })}
          >
            <MenuItem value="">All governing bodies</MenuItem>
            {governingBodies.map((gb) => (
              <MenuItem key={gb.id} value={String(gb.id)}>
                {gb.abbreviation ? `${gb.abbreviation} — ${gb.name}` : gb.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Status"
            variant="outlined"
            size="small"
            className={classes.filterFieldNarrow}
            value={state.status}
            onChange={(e) => patch({ status: e.target.value })}
          >
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="DEPRECATED">Deprecated</MenuItem>
            <MenuItem value="ALL">All</MenuItem>
          </TextField>
        </Paper>

        <TableContainer component={Paper}>
          <Table size="small" aria-label="Rule groups" aria-busy={loading}>
            <TableHead>
              <TableRow>
                {SORTABLE.map((col) => (
                  <TableCell
                    key={col.key}
                    scope="col"
                    className={`${classes.headerCell}${
                      col.key === "Id" ? ` ${classes.fit}` : ""
                    }`}
                    sortDirection={sortDirection(state, col.key)}
                  >
                    <TableSortLabel
                      active={state.sort === col.key}
                      direction={sortDirection(state, col.key) || "asc"}
                      onClick={() => onSort(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell
                  scope="col"
                  className={`${classes.headerCell} ${classes.gbColumn}`}
                >
                  Governing bodies
                </TableCell>
                <TableCell
                  scope="col"
                  className={`${classes.headerCell} ${classes.fit}`}
                  sortDirection={sortDirection(state, "RuleSetCount")}
                >
                  <TableSortLabel
                    active={state.sort === "RuleSetCount"}
                    direction={sortDirection(state, "RuleSetCount") || "asc"}
                    onClick={() => onSort("RuleSetCount")}
                  >
                    Rule sets
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  scope="col"
                  className={`${classes.headerCell} ${classes.fit}`}
                  sortDirection={sortDirection(state, "Status")}
                >
                  <TableSortLabel
                    active={state.sort === "Status"}
                    direction={sortDirection(state, "Status") || "asc"}
                    onClick={() => onSort("Status")}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  scope="col"
                  className={`${classes.headerCell} ${classes.fit}`}
                  sortDirection={sortDirection(state, "UpdatedAt")}
                >
                  <TableSortLabel
                    active={state.sort === "UpdatedAt"}
                    direction={sortDirection(state, "UpdatedAt") || "asc"}
                    onClick={() => onSort("UpdatedAt")}
                  >
                    Updated
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  scope="col"
                  className={`${classes.headerCell} ${classes.fit}`}
                >
                  <span className="sr-only">Actions</span>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box className={classes.progressWrap}>
                      <CircularProgress aria-label="Loading rule groups" />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className={classes.empty}>
                    No rule groups match these filters.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell className={`${classes.cell} ${classes.number}`}>
                      {row.id}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <RouterLink
                        to={rulesPath.sets(row.id)}
                        state={returnState(location)}
                      >
                        {row.name}
                      </RouterLink>
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {row.governingBodies?.length ? (
                        <ul
                          className={classes.chips}
                          aria-label={`Governing bodies of ${row.name}`}
                        >
                          {row.governingBodies.map((gb) => (
                            <li key={gb.id}>
                              <Chip
                                size="small"
                                label={gb.abbreviation || gb.shortName || gb.id}
                                title={gb.shortName || undefined}
                              />
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <Chip
                          size="small"
                          label="none"
                          className={classes.chipNeutral}
                        />
                      )}
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.number}`}>
                      <RouterLink
                        to={rulesPath.sets(row.id)}
                        state={returnState(location)}
                        aria-label={`${row.ruleSetCount} rule sets in ${row.name}`}
                      >
                        {row.ruleSetCount}
                      </RouterLink>
                      {row.status === "DEPRECATED" &&
                        row.activeRuleSetCount > 0 && (
                          <Chip
                            size="small"
                            className={classes.chipWarn}
                            style={{ marginLeft: 6 }}
                            label={`${row.activeRuleSetCount} still active`}
                            title="Active rule sets inside a deprecated group are still hidden from meet setup."
                          />
                        )}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <StatusChip status={row.status} />
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <AuditStamp audit={row.audit} />
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <IconButton
                        className={classes.iconButton}
                        aria-label={`Edit ${row.name}`}
                        onClick={() => setDialog({ open: true, group: row })}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <ListPagination
            filters={{
              page: result.currentPage,
              limit: state.size,
              total: result.totalElements,
            }}
            fetchFunction={({ page, limit }) =>
              setState((prev) => ({
                ...prev,
                page,
                size: Number(limit) || DEFAULT_SIZE,
              }))
            }
          />
        </TableContainer>
      </Container>

      <RuleGroupDialog
        open={dialog.open}
        group={dialog.group}
        onClose={() => setDialog({ open: false, group: null })}
        onSaved={() => {
          setDialog({ open: false, group: null });
          load();
        }}
      />
    </Box>
  );
};

export default RuleGroups;
